import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { estimateApi, leadApi, priceListApi, estimateCategoryApi, promotionApi, contractApi } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '../components/ui/alert-dialog';
import { ElectricalWizardModal } from '../components/estimates/ElectricalWizardModal';
import { formatCurrency } from '../lib/utils';

// Import extracted components
import {
  EstimateHeader,
  EstimateLeadPromoCard,
  EstimateCategoryTabs,
  EstimateCategoryContent,
  EstimateSummaryCard,
  EstimateAddItemModal,
  VersionsModal,
  CreateVersionModal,
  PreviewModal,
  CATEGORIES
} from '../components/estimates';

// STEP 1: Helper per inserire una nuova voce dopo un id specifico (o in fondo se id null)
const insertItemRespectingPosition = (items, newItem, afterId) => {
  if (!afterId) return [...items, newItem];
  const idx = items.findIndex(i => i.id === afterId);
  if (idx === -1) return [...items, newItem];
  const next = [...items];
  next.splice(idx + 1, 0, newItem);
  return next;
};

const EstimateDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { registerGuard, unregisterGuard, setBlocking, attemptNavigation } = useNavigationGuard();
  
  const isNew = id === 'new';
  const preselectedLeadId = searchParams.get('lead_id');
  
  // State
  const [estimate, setEstimate] = useState(null);
  const [lead, setLead] = useState(null);
  const [leads, setLeads] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialFormDataRef = useRef(null);
  // FASE 5F: reorder bloccato in presenza di modifiche non salvate
  const [reorderConfirm, setReorderConfirm] = useState(null); // { category, orderedItemIds }
  
  // Note editing state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  
  // UI State
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showElectricalWizard, setShowElectricalWizard] = useState(false);
  // STEP 1: Posizione di inserimento per "Aggiungi sotto" (null = aggiungi in fondo)
  const [insertAfterId, setInsertAfterId] = useState(null);
  // FASE 5D: ambiente di destinazione per "Aggiungi voce" dentro un gruppo elettrico
  const [addItemRoomContext, setAddItemRoomContext] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [creatingContract, setCreatingContract] = useState(false);
  
  // Versioning State
  const [versions, setVersions] = useState([]);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showCreateVersionModal, setShowCreateVersionModal] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [creatingVersion, setCreatingVersion] = useState(false);
  
  // Document Preview State
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState('customer');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Sync with navigation guard context
  useEffect(() => {
    setBlocking(hasUnsavedChanges);
  }, [hasUnsavedChanges, setBlocking]);

  // Form data
  const [formData, setFormData] = useState({
    lead_id: preselectedLeadId || '',
    technician_id: user?.id || '',
    validity_days: 30,
    delivery_days: null,
    signing_conditions: '',
    notes: '',
    items: [],
    category_discounts: [],
    category_notes: {},
    promotion_id: null,
    promotion_discount_percentage: 0,
    works_discount_mode: 'percentage',
    works_discount_percentage: 0,
    works_closed_price: null
  });

  // Refs for navigation guard
  const formDataRef = useRef(formData);
  const grandTotalRef = useRef(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // New item form
  const [newItem, setNewItem] = useState({
    price_item_id: '',
    code: '',
    description: '',
    category: '',
    subcategory: '',
    quantity: 1,
    quantity_formula: '',
    unit: '',
    unit_price: 0,
    notes: ''
  });

  // Save function for navigation guard
  const handleSaveForGuard = useCallback(async () => {
    const currentFormData = formDataRef.current;
    const currentGrandTotal = grandTotalRef.current;
    
    if (!currentFormData.lead_id) {
      toast.error('Seleziona un lead');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...currentFormData,
        subtotal: currentGrandTotal?.subtotal || 0,
        total: currentGrandTotal?.total || 0
      };
      
      if (isNew) {
        await estimateApi.create(payload);
        toast.success('Preventivo creato');
      } else {
        await estimateApi.update(id, payload);
        toast.success('Preventivo salvato');
      }
      setHasUnsavedChanges(false);
      initialFormDataRef.current = JSON.stringify(currentFormData);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }, [isNew, id]);

  // Register navigation guard
  useEffect(() => {
    registerGuard({
      onSave: handleSaveForGuard,
      onConfirm: (path) => navigate(path)
    });
    return () => unregisterGuard();
  }, [registerGuard, unregisterGuard, navigate, handleSaveForGuard]);

  // Safe navigation
  const safeNavigate = useCallback((path) => {
    if (hasUnsavedChanges) {
      attemptNavigation(path);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate, attemptNavigation]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        const catRes = await estimateCategoryApi.getAll();
        setCategories(catRes.data);
        
        const promoRes = await promotionApi.getAll();
        setPromotions(promoRes.data);
        
        const leadsRes = await leadApi.getAll();
        setLeads(leadsRes.data);
        
        const priceRes = await priceListApi.getAll();
        setPriceList(priceRes.data);
        
        if (!isNew) {
          const estRes = await estimateApi.getOne(id);
          setEstimate(estRes.data);
          
          if (estRes.data.lead_id) {
            const leadRes = await leadApi.getOne(estRes.data.lead_id);
            setLead(leadRes.data);
          }
          
          const loadedFormData = {
            lead_id: estRes.data.lead_id,
            technician_id: estRes.data.technician_id,
            validity_days: estRes.data.validity_days || 30,
            delivery_days: estRes.data.delivery_days || null,
            signing_conditions: estRes.data.signing_conditions || '',
            notes: estRes.data.notes || '',
            items: estRes.data.items || [],
            category_discounts: estRes.data.category_discounts || [],
            category_notes: estRes.data.category_notes || {},
            promotion_id: estRes.data.promotion_id || null,
            promotion_discount_percentage: estRes.data.promotion_discount_percentage || 0,
            works_discount_mode: estRes.data.works_discount_mode || 'percentage',
            works_discount_percentage: estRes.data.works_discount_percentage || 0,
            works_closed_price: estRes.data.works_closed_price ?? null
          };
          setFormData(loadedFormData);
          initialFormDataRef.current = JSON.stringify(loadedFormData);
          setHasUnsavedChanges(false);
          
          const firstCatWithItems = CATEGORIES.find(cat => 
            (estRes.data.items || []).some(item => item.category === cat.code)
          );
          if (firstCatWithItems) {
            setActiveCategory(firstCatWithItems.code);
          }
        } else {
          const defaultPromo = promoRes.data.find(p => p.is_default);
          if (defaultPromo) {
            setFormData(prev => ({
              ...prev,
              promotion_id: defaultPromo.id,
              promotion_discount_percentage: defaultPromo.discount_percentage
            }));
          }
          setHasUnsavedChanges(true);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isNew]);

  // Load versions
  const loadVersions = async () => {
    if (!isNew && id) {
      try {
        const res = await estimateApi.getVersions(id);
        setVersions(res.data);
      } catch (error) {
        console.error('Error loading versions:', error);
      }
    }
  };

  useEffect(() => {
    if (!isNew && id && estimate) {
      loadVersions();
    }
  }, [id, isNew, estimate]);

  // Track changes
  useEffect(() => {
    if (!loading && initialFormDataRef.current && !isNew) {
      const currentData = JSON.stringify(formData);
      if (currentData !== initialFormDataRef.current) {
        setHasUnsavedChanges(true);
      }
    }
  }, [formData, loading, isNew]);

  // Calculate category totals
  const categoryTotals = useMemo(() => {
    const totals = {};
    
    CATEGORIES.forEach(cat => {
      const categoryItems = formData.items.filter(item => item.category === cat.code);
      const subtotal = categoryItems.reduce((sum, item) => sum + (item.total || 0), 0);
      
      const discountConfig = formData.category_discounts.find(d => d.category === cat.code);
      const discountMode = discountConfig?.discount_mode || 'percentage';
      const discountPercentage = discountConfig?.discount_percentage || 0;
      const fixedPrice = discountConfig?.fixed_price;
      
      let discountAmount = 0;
      let discountedTotal = subtotal;
      let calculatedPercentage = discountPercentage;
      let calculatedFixedPrice = subtotal;

      // === SCONTO UNICO LAVORI C+D+E ===
      // Nuovo schema: C/D/E solo lordo + sconto unico lavori.
      // Legacy: un preventivo storico con sconto per singola categoria C/D/E e
      // senza sconto lavori conserva il comportamento per-categoria.
      const worksActive = (formData.works_discount_percentage > 0) ||
        (formData.works_closed_price !== null && formData.works_closed_price !== undefined && formData.works_closed_price !== '');
      const hasLegacyCatDiscount = !!discountConfig && (
        (discountConfig.discount_percentage || 0) > 0 ||
        (discountConfig.fixed_price !== null && discountConfig.fixed_price !== undefined)
      );
      const isWorksCategory = ['C', 'D', 'E'].includes(cat.code) && (worksActive || !hasLegacyCatDiscount);

      if (!isWorksCategory && subtotal > 0) {
        if (discountMode === 'fixed_price' && fixedPrice !== null && fixedPrice !== undefined) {
          // Fixed price mode: calculate from fixed price
          discountedTotal = Math.max(0, Math.min(fixedPrice, subtotal)); // Clamp between 0 and subtotal
          discountAmount = subtotal - discountedTotal;
          calculatedPercentage = (discountAmount / subtotal) * 100;
          calculatedFixedPrice = discountedTotal;
        } else if (discountPercentage > 0) {
          // Percentage mode: calculate from percentage
          discountAmount = subtotal * (discountPercentage / 100);
          discountedTotal = subtotal - discountAmount;
          calculatedPercentage = discountPercentage;
          calculatedFixedPrice = discountedTotal;
        }
      }
      
      totals[cat.code] = {
        itemCount: categoryItems.length,
        subtotal,
        discountMode,
        discountPercentage: calculatedPercentage,
        discountPercentageDisplay: Math.round(calculatedPercentage * 100) / 100, // Max 2 decimals
        fixedPrice: calculatedFixedPrice,
        discountAmount,
        discountedTotal,
        total: discountedTotal
      };
    });
    
    return totals;
  }, [formData.items, formData.category_discounts, formData.works_discount_percentage, formData.works_closed_price]);

  // === SCONTO UNICO LAVORI C+D+E ===
  const worksTotals = useMemo(() => {
    const base = ['C', 'D', 'E'].reduce((sum, code) => sum + (categoryTotals[code]?.subtotal || 0), 0);
    const anyCDE = ['C', 'D', 'E'].some(code => (categoryTotals[code]?.itemCount || 0) > 0);
    const mode = formData.works_discount_mode || 'percentage';
    const pctInput = formData.works_discount_percentage || 0;
    const closedInput = formData.works_closed_price;
    const worksActive = (pctInput > 0) || (closedInput !== null && closedInput !== undefined && closedInput !== '');
    // Legacy: C/D/E con sconto per-categoria e senza sconto lavori -> blocco nascosto.
    const legacyMode = !worksActive && ['C', 'D', 'E'].some(code => {
      const dc = (formData.category_discounts || []).find(d => d.category === code);
      return dc && ((dc.discount_percentage || 0) > 0 || (dc.fixed_price !== null && dc.fixed_price !== undefined));
    });
    const present = anyCDE && !legacyMode;

    let discountAmount = 0;
    let netTotal = base;
    let percentage = pctInput;

    if (base > 0) {
      if (mode === 'closed_price' && closedInput !== null && closedInput !== undefined && closedInput !== '') {
        const closed = Math.max(0, Math.min(Number(closedInput), base));
        netTotal = closed;
        discountAmount = base - closed;
        percentage = (discountAmount / base) * 100;
      } else if (pctInput > 0) {
        discountAmount = base * (pctInput / 100);
        netTotal = base - discountAmount;
        percentage = pctInput;
      }
    }

    return {
      present,
      base,
      mode,
      percentage,
      percentageDisplay: Math.round(percentage * 100) / 100,
      closedPrice: netTotal,
      discountAmount,
      netTotal,
      hasDiscount: Math.round(discountAmount * 100) / 100 !== 0,
    };
  }, [categoryTotals, formData.category_discounts, formData.works_discount_mode, formData.works_discount_percentage, formData.works_closed_price]);

  // Grand total
  const grandTotal = useMemo(() => {
    let subtotalBeforeCategoryDiscounts = 0;
    let totalCategoryDiscount = 0;
    let subtotalAfterCategoryDiscounts = 0;
    
    Object.values(categoryTotals).forEach(cat => {
      subtotalBeforeCategoryDiscounts += cat.subtotal;
      totalCategoryDiscount += cat.discountAmount;
      subtotalAfterCategoryDiscounts += cat.discountedTotal;
    });

    // Sconto unico lavori C+D+E (sottratto dall'aggregato)
    const worksDiscount = worksTotals.discountAmount || 0;
    totalCategoryDiscount += worksDiscount;
    subtotalAfterCategoryDiscounts -= worksDiscount;

    const promoPercentage = formData.promotion_discount_percentage || 0;
    const promoDiscount = subtotalAfterCategoryDiscounts * (promoPercentage / 100);
    const subtotalAfterAllDiscounts = subtotalAfterCategoryDiscounts - promoDiscount;
    
    const result = {
      subtotal: subtotalBeforeCategoryDiscounts,
      categoryDiscount: totalCategoryDiscount,
      worksDiscount,
      subtotalAfterCategoryDiscounts,
      promoDiscount,
      promoPercentage,
      total: subtotalAfterAllDiscounts
    };
    
    grandTotalRef.current = result;
    return result;
  }, [categoryTotals, worksTotals, formData.promotion_discount_percentage]);

  // Categories with items
  const categoriesWithItems = useMemo(() => {
    return CATEGORIES.filter(cat => categoryTotals[cat.code]?.itemCount > 0);
  }, [categoryTotals]);

  // Handle promotion change
  const handlePromotionChange = (promotionId) => {
    const promo = promotions.find(p => p.id === promotionId);
    setFormData(prev => ({
      ...prev,
      promotion_id: promotionId,
      promotion_discount_percentage: promo?.discount_percentage || 0
    }));
  };

  // Handle category discount change (percentage mode)
  const handleCategoryDiscountChange = (categoryCode, value) => {
    const percentage = parseFloat(value) || 0;
    
    setFormData(prev => {
      const existingIndex = prev.category_discounts.findIndex(d => d.category === categoryCode);
      let newDiscounts = [...prev.category_discounts];
      
      const newDiscount = {
        category: categoryCode,
        discount_percentage: percentage,
        fixed_price: null, // Will be calculated
        discount_mode: 'percentage'
      };
      
      if (existingIndex >= 0) {
        newDiscounts[existingIndex] = { ...newDiscounts[existingIndex], ...newDiscount };
      } else {
        newDiscounts.push(newDiscount);
      }
      
      return { ...prev, category_discounts: newDiscounts };
    });
  };

  // Handle category fixed price change (fixed_price mode)
  const handleCategoryFixedPriceChange = (categoryCode, value) => {
    const fixedPrice = parseFloat(value);
    
    // Validate: fixed price cannot be negative
    if (fixedPrice < 0) return;
    
    setFormData(prev => {
      const existingIndex = prev.category_discounts.findIndex(d => d.category === categoryCode);
      let newDiscounts = [...prev.category_discounts];
      
      const newDiscount = {
        category: categoryCode,
        discount_percentage: 0, // Will be calculated
        fixed_price: isNaN(fixedPrice) ? null : fixedPrice,
        discount_mode: 'fixed_price'
      };
      
      if (existingIndex >= 0) {
        newDiscounts[existingIndex] = { ...newDiscounts[existingIndex], ...newDiscount };
      } else {
        newDiscounts.push(newDiscount);
      }
      
      return { ...prev, category_discounts: newDiscounts };
    });
  };

  // === SCONTO UNICO LAVORI C+D+E ===
  const handleWorksDiscountChange = (value) => {
    const percentage = parseFloat(value);
    setFormData(prev => ({
      ...prev,
      works_discount_mode: 'percentage',
      works_discount_percentage: isNaN(percentage) ? 0 : Math.max(0, percentage),
      works_closed_price: null
    }));
  };

  const handleWorksClosedPriceChange = (value) => {
    const closed = parseFloat(value);
    if (closed < 0) return;
    setFormData(prev => ({
      ...prev,
      works_discount_mode: 'closed_price',
      works_closed_price: isNaN(closed) ? null : closed,
      works_discount_percentage: 0
    }));
  };


  // Handle category notes change
  const handleCategoryNotesChange = (categoryCode, value) => {
    setFormData(prev => ({
      ...prev,
      category_notes: { ...prev.category_notes, [categoryCode]: value }
    }));
  };

  // Add item from price list
  const handleAddItemFromPriceList = (priceItem) => {
    if (!activeCategory) {
      toast.error('Seleziona prima una categoria');
      return;
    }

    const isElectrical = activeCategory === 'D';
    // Elettrico: duplicati ammessi (es. più prese Schuko). Altre categorie: guardia invariata.
    if (!isElectrical) {
      const alreadyExists = formData.items.some(item => item.price_item_id === priceItem.id);
      if (alreadyExists) {
        toast.error('Questa voce è già presente nel preventivo');
        return;
      }
    }

    const roomCtx = addItemRoomContext;
    const qty = newItem.quantity || 1;
    // Per l'elettrico ricava serie/tecnologia/variante/prezzo da una riga D esistente
    // (preferendo lo stesso codice per avere il prezzo corretto per la serie del preventivo).
    const refRow = isElectrical
      ? (formData.items.find(i => i.category === 'D' && i.code === priceItem.code && (i.selected_series || i.pricing_variant))
         || formData.items.find(i => i.category === 'D' && (i.selected_series || i.electrical_preset)))
      : null;
    const sameCodeRow = isElectrical
      ? formData.items.find(i => i.category === 'D' && i.code === priceItem.code && i.unit_price != null)
      : null;
    const unitPrice = (sameCodeRow && sameCodeRow.unit_price != null)
      ? sameCodeRow.unit_price
      : priceItem.unit_price;

    const newItemData = {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `item-${Date.now()}`,
      price_item_id: priceItem.id,
      catalog_item_id: priceItem.id,
      code: priceItem.code,
      description: priceItem.description,
      category: activeCategory,
      subcategory: priceItem.subcategory || selectedSubcategory,
      quantity: qty,
      quantity_formula: newItem.quantity_formula || '',
      unit: priceItem.unit,
      unit_price: unitPrice,
      total: unitPrice * qty,
      notes: ''
    };

    if (isElectrical) {
      const preset = (refRow && refRow.electrical_preset) || null;
      if (refRow) {
        if (refRow.selected_series) newItemData.selected_series = refRow.selected_series;
        if (refRow.selected_technology) newItemData.selected_technology = refRow.selected_technology;
        // Copia la variante di prezzo solo da una riga con lo STESSO master: variant diverse
        // appartengono a funzioni diverse (es. D07 connessa vs D01). Il backend/catalogo resta la verità.
        if (sameCodeRow && sameCodeRow.pricing_variant) newItemData.pricing_variant = sameCodeRow.pricing_variant;
        if (preset) newItemData.electrical_preset = preset;
      }
      // Assegna l'ambiente: dal contesto gruppo se presente, altrimenti "altre"
      const key = roomCtx?.key || 'altre';
      const label = roomCtx?.label || 'Altre lavorazioni elettriche';
      const scope = (key === 'generali' || key === 'altre') ? 'general' : 'room';
      newItemData.electrical_room_key = key;
      newItemData.electrical_room_label = label;
      newItemData.electrical_scope = scope;
      newItemData.electrical_group_id = roomCtx?.groupId || `${preset || 'personalizzato'}:${key}`;
    }

    setFormData(prev => ({
      ...prev,
      items: insertItemRespectingPosition(prev.items, newItemData, insertAfterId)
    }));

    setInsertAfterId(null);
    setAddItemRoomContext(null);
    setNewItem(prev => ({ ...prev, quantity: 1, quantity_formula: '' }));
    toast.success('Voce aggiunta');
  };

  // Check if item in estimate
  const isItemInEstimate = (priceItemId) => {
    // Elettrico (categoria D): duplicati ammessi, nessuna disabilitazione
    if (activeCategory === 'D') return false;
    return formData.items.some(item => item.price_item_id === priceItemId);
  };

  // Add manual item
  const handleAddManualItem = () => {
    if (!activeCategory || !newItem.description || !newItem.unit_price) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    const total = newItem.quantity * newItem.unit_price;
    const newItemData = {
      id: `item-${Date.now()}`,
      price_item_id: null,
      code: newItem.code || `MAN-${Date.now()}`,
      description: newItem.description,
      category: activeCategory,
      subcategory: newItem.subcategory || selectedSubcategory,
      quantity: newItem.quantity,
      quantity_formula: newItem.quantity_formula || '',
      unit: newItem.unit,
      unit_price: newItem.unit_price,
      total,
      notes: newItem.notes
    };
    
    setFormData(prev => ({
      ...prev,
      items: insertItemRespectingPosition(prev.items, newItemData, insertAfterId)
    }));
    
    setInsertAfterId(null);
    setNewItem({
      price_item_id: '',
      code: '',
      description: '',
      category: '',
      subcategory: '',
      quantity: 1,
      quantity_formula: '',
      unit: '',
      unit_price: 0,
      notes: ''
    });
    
    setShowAddItemModal(false);
    toast.success('Voce aggiunta');
  };

  // Update item
  const handleUpdateItem = (itemId, field, value, formula = '') => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' && formula) {
            updated.quantity_formula = formula;
          } else if (field === 'quantity' && !formula) {
            updated.quantity_formula = '';
          }
          if (field === 'quantity' || field === 'unit_price') {
            updated.total = updated.quantity * updated.unit_price;
          }
          return updated;
        }
        return item;
      })
    }));
  };

  // Remove item
  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    toast.success('Voce rimossa');
  };

  // V1 Riordino voci intra-categoria (drag&drop + frecce)
  // Condizione: preventivo non in stato finale + nessun progetto collegato + utente autorizzato.
  const REORDER_BLOCKED_STATUSES = ['firmato', 'rifiutato', 'validato', 'convertito', 'in_attesa_validazione_admin'];
  const canReorderItems =
    !!estimate &&
    !REORDER_BLOCKED_STATUSES.includes(estimate?.status) &&
    !estimate?.project_id &&
    (user?.role === 'admin' ||
     user?.role === 'project_manager' ||
     user?.role === 'administration' ||
     (user?.role === 'external_technician' && estimate?.technician_id === user?.id));

  const executeReorder = async (category, orderedItemIds, sourceItems = null) => {
    // Optimistic UI update. sourceItems permette di riordinare partendo dallo stato
    // fresco del server (usato dopo "Salva e continua") invece della closure locale.
    const baseItems = sourceItems || formData.items;
    const itemsById = Object.fromEntries(baseItems.map(it => [it.id, it]));
    // Ricostruisci l'array preservando la posizione originale delle voci di altre categorie
    const newItems = [];
    const reorderIter = orderedItemIds[Symbol.iterator]();
    for (const it of baseItems) {
      if (it.category === category) {
        const nextId = reorderIter.next().value;
        newItems.push(itemsById[nextId]);
      } else {
        newItems.push(it);
      }
    }
    const newFormData = { ...formData, items: newItems };
    setFormData(newFormData);
    // Persist via endpoint dedicato
    try {
      await estimateApi.reorderItems(id, category, orderedItemIds);
      // Ordine persistito: allinea il baseline per non lasciare uno stato "dirty" fittizio
      initialFormDataRef.current = JSON.stringify(newFormData);
      setHasUnsavedChanges(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Errore riordino voci';
      toast.error(detail);
      // Rollback UI: ricarica dal server
      try {
        const fresh = await estimateApi.getOne(id);
        setEstimate(fresh.data);
        setFormData(prev => ({ ...prev, items: fresh.data.items || [] }));
      } catch { /* ignore reload error */ }
    }
  };

  const handleReorderItems = async (category, orderedItemIds) => {
    // FASE 5F: se esistono modifiche non salvate, NON riordinare subito.
    // Chiedi esplicitamente di salvare prima (evita la perdita di modifiche pendenti).
    if (hasUnsavedChanges) {
      setReorderConfirm({ category, orderedItemIds });
      return;
    }
    await executeReorder(category, orderedItemIds);
  };

  // FASE 5F: "Salva e continua" -> validazione + salvataggio + conferma + reorder
  const handleReorderSaveAndContinue = async () => {
    const pending = reorderConfirm;
    setReorderConfirm(null);
    if (!pending) return;
    const ok = await handleSave();
    if (!ok) {
      // Salvataggio fallito: NON eseguire il reorder.
      return;
    }
    // Riordina partendo dallo stato fresco del server (evita di scartare campi calcolati)
    let freshItems = null;
    try {
      const fresh = await estimateApi.getOne(id);
      freshItems = fresh.data.items || null;
    } catch { /* fallback: usa lo stato locale */ }
    await executeReorder(pending.category, pending.orderedItemIds, freshItems);
  };

  // Save estimate
  const handleSave = async (newStatus = null) => {
    if (!formData.lead_id) {
      toast.error('Seleziona un lead');
      return false;
    }
    
    // STEP 2: Validazione modalità "detailed" - blocca salvataggio se ci sono voci
    // con dettaglio misure attivo ma senza righe valide
    const invalidDetailed = formData.items.filter(item => {
      if ((item.quantity_mode || 'single') !== 'detailed') return false;
      const lines = Array.isArray(item.measurement_lines) ? item.measurement_lines : [];
      const totalQty = lines.reduce((sum, l) => sum + (l.calculated_quantity || 0), 0);
      return lines.length === 0 || totalQty <= 0;
    });
    if (invalidDetailed.length > 0) {
      const first = invalidDetailed[0];
      toast.error(
        `Voce "${first.code || first.description?.substring(0, 30) || ''}": dettaglio misure attivo ma senza righe valide. Inserisci almeno una riga misura valida oppure torna a Quantità singola.`,
        { duration: 6000 }
      );
      return false;
    }
    
    setSaving(true);
    let saveOk = false;
    try {
      const payload = {
        ...formData,
        subtotal: grandTotal.subtotal,
        total: grandTotal.total
      };
      
      if (newStatus) {
        payload.status = newStatus;
      }
      
      if (isNew) {
        const res = await estimateApi.create(payload);
        toast.success('Preventivo creato');
        setHasUnsavedChanges(false);
        navigate(`/estimates/${res.data.id}`, { replace: true });
      } else {
        await estimateApi.update(id, payload);
        toast.success('Preventivo salvato');
        initialFormDataRef.current = JSON.stringify(formData);
        setHasUnsavedChanges(false);
        const estRes = await estimateApi.getOne(id);
        setEstimate(estRes.data);
        setFormData(prev => ({
          ...prev,
          items: estRes.data.items || [],
          subtotal: estRes.data.subtotal,
          total: estRes.data.total
        }));
      }
      saveOk = true;
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Errore nel salvataggio');
      saveOk = false;
    } finally {
      setSaving(false);
    }
    return saveOk;
  };

  // Download PDF - USA NUOVO DOCUMENT ENGINE
  const handleDownloadPdf = async (type) => {
    setDownloadingPdf(type);
    try {
      // Map frontend type to new Document Engine render_mode
      // full/interno → detailed, client/cliente → customer
      const renderMode = type === 'full' ? 'detailed' : 'customer';
      
      const response = await fetch(
        `/api/documents/estimates/${id}/pdf/${renderMode}`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}` 
          },
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF error:', errorText);
        throw new Error('PDF generation failed');
      }
      
      const blob = await response.blob();
      const filename = `${estimate?.estimate_number || 'preventivo'}_${type === 'full' ? 'completo' : 'cliente'}.pdf`.replace('/', '-');
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`PDF ${type === 'full' ? 'completo' : 'cliente'} scaricato`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Preview document
  const handlePreview = async (mode) => {
    setLoadingPreview(true);
    setPreviewMode(mode);
    try {
      const response = await fetch(
        `/api/html-documents/estimates/${id}/preview?render_mode=${mode}`,
        { headers: { withCredentials: true } }
      );
      
      if (!response.ok) throw new Error('Preview failed');
      
      const html = await response.text();
      setPreviewHtml(html);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Errore nel caricamento dell\'anteprima');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Create contract
  const handleCreateContract = async () => {
    if (!estimate || !formData.lead_id) {
      toast.error('Salva prima il preventivo');
      return;
    }
    
    setCreatingContract(true);
    try {
      const existingContracts = await contractApi.getAll();
      const existingContract = existingContracts.data.find(c => c.estimate_id === id);
      
      if (existingContract) {
        toast.info('Contratto già esistente, apertura...');
        navigate(`/contracts/${existingContract.id}`);
        return;
      }
      
      const res = await contractApi.create({
        estimate_id: id,
        lead_id: formData.lead_id
      });
      toast.success('Contratto generato');
      navigate(`/contracts/${res.data.id}`);
    } catch (error) {
      console.error('Error creating contract:', error);
      const msg = error.response?.data?.detail || 'Errore nella creazione del contratto';
      toast.error(msg);
    } finally {
      setCreatingContract(false);
    }
  };

  // Create version
  const handleCreateVersion = async () => {
    if (hasUnsavedChanges) {
      toast.error('Salva prima le modifiche correnti');
      return;
    }
    
    setCreatingVersion(true);
    try {
      const res = await estimateApi.createVersion(id, versionNote);
      toast.success(`Versione ${res.data.version} creata`);
      setShowCreateVersionModal(false);
      setVersionNote('');
      navigate(`/estimates/${res.data.id}`);
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Errore nella creazione della versione');
    } finally {
      setCreatingVersion(false);
    }
  };

  // Get lead name
  const getLeadName = (leadId) => {
    const l = leads.find(l => l.id === leadId);
    return l ? `${l.first_name} ${l.last_name}` : 'N/D';
  };

  // Filtered price list
  const filteredPriceList = useMemo(() => {
    return priceList.filter(item => {
      if (activeCategory && item.category !== activeCategory) return false;
      
      const matchesSearch = !searchTerm || 
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSubcategory = selectedSubcategory === 'all' || 
        item.subcategory === selectedSubcategory;
      
      return matchesSearch && matchesSubcategory;
    });
  }, [priceList, searchTerm, activeCategory, selectedSubcategory]);

  // Active subcategories
  const activeSubcategories = useMemo(() => {
    const cat = categories.find(c => c.code === activeCategory);
    return cat?.subcategories || [];
  }, [categories, activeCategory]);

  // Active category items
  const activeCategoryItems = useMemo(() => {
    if (!activeCategory) return [];
    return formData.items.filter(item => item.category === activeCategory);
  }, [formData.items, activeCategory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF7819]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estimate-detail-page">
      {/* Header */}
      <EstimateHeader
        isNew={isNew}
        estimate={estimate}
        lead={lead}
        versions={versions}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        downloadingPdf={downloadingPdf}
        creatingContract={creatingContract}
        loadingPreview={loadingPreview}
        formData={formData}
        onBack={() => safeNavigate(estimate?.lead_id ? `/leads/${estimate.lead_id}` : '/leads')}
        onSave={() => handleSave()}
        onDownloadPdf={handleDownloadPdf}
        onPreview={handlePreview}
        onCreateContract={handleCreateContract}
        onShowVersions={() => setShowVersionsModal(true)}
        onCreateVersion={() => setShowCreateVersionModal(true)}
      />

      {/* Lead and Promotion Card */}
      <EstimateLeadPromoCard
        isNew={isNew}
        formData={formData}
        setFormData={setFormData}
        leads={leads}
        promotions={promotions}
        onPromotionChange={handlePromotionChange}
        getLeadName={getLeadName}
      />

      {/* Category Tabs */}
      <EstimateCategoryTabs
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        categoryTotals={categoryTotals}
      />

      {/* FASE 4: trigger wizard impianto elettrico (categoria D) */}
      {activeCategory === 'D' && !isNew && (
        <div className="my-3">
          <Button
            onClick={() => setShowElectricalWizard(true)}
            data-testid="open-electrical-wizard-btn"
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Zap className="h-4 w-4 mr-2" /> Configura impianto elettrico
          </Button>
        </div>
      )}

      {/* Category Content */}
      {activeCategory && (
        <EstimateCategoryContent
          activeCategory={activeCategory}
          activeCategoryItems={activeCategoryItems}
          categoryTotals={categoryTotals}
          grandTotal={grandTotal}
          formData={formData}
          editingNoteId={editingNoteId}
          setEditingNoteId={setEditingNoteId}
          editingNoteValue={editingNoteValue}
          setEditingNoteValue={setEditingNoteValue}
          onShowAddItemModal={() => { setInsertAfterId(null); setShowAddItemModal(true); }}
          onAddBelow={(itemId) => { setInsertAfterId(itemId); setShowAddItemModal(true); }}
          onUpdateItem={handleUpdateItem}
          onRemoveItem={handleRemoveItem}
          onCategoryNotesChange={handleCategoryNotesChange}
          onCategoryDiscountChange={handleCategoryDiscountChange}
          onCategoryFixedPriceChange={handleCategoryFixedPriceChange}
          canReorder={canReorderItems}
          onReorder={handleReorderItems}
          onAddItemToRoom={(key, label, groupId) => {
            setInsertAfterId(null);
            setAddItemRoomContext({ key, label, groupId });
            setShowAddItemModal(true);
          }}
        />
      )}

      {/* STEP 1: Sticky action bar - solo se c'è una categoria attiva con voci */}
      {activeCategory && activeCategoryItems.length > 0 && (
        <div
          className="sticky bottom-0 -mx-4 lg:-mx-6 bg-white border-t border-[#DCDCDC] shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.08)] z-30 px-4 lg:px-6 py-2"
          data-testid="estimate-sticky-bar"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button
              size="sm"
              onClick={() => { setInsertAfterId(null); setShowAddItemModal(true); }}
              className="bg-[#FF7819] hover:bg-[#e66a0d] text-white"
              data-testid="add-item-btn-sticky"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi voce
            </Button>
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-1 hidden sm:flex">
                <span className="text-[#737373]">Tot. {activeCategory}:</span>
                <span className="font-semibold text-[#3C3C3C]" data-testid="sticky-category-total">
                  {formatCurrency(
                    categoryTotals?.[activeCategory]?.discountedTotal
                    ?? categoryTotals?.[activeCategory]?.subtotal
                    ?? 0
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[#737373]">Tot. preventivo:</span>
                <span className="font-bold text-[#FF7819] text-base" data-testid="sticky-grand-total">
                  {formatCurrency(grandTotal?.total || 0)}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => handleSave()}
                disabled={saving}
                className="bg-[#3C3C3C] hover:bg-[#2a2a2a] text-white"
                data-testid="save-btn-sticky"
              >
                {saving ? 'Salvataggio...' : (hasUnsavedChanges ? 'Salva modifiche' : 'Salva')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Card */}
      <EstimateSummaryCard
        categoriesWithItems={categoriesWithItems}
        categoryTotals={categoryTotals}
        grandTotal={grandTotal}
        formData={formData}
        promotions={promotions}
        worksTotals={worksTotals}
        onWorksDiscountChange={handleWorksDiscountChange}
        onWorksClosedPriceChange={handleWorksClosedPriceChange}
      />

      {/* Add Item Modal */}
      <EstimateAddItemModal
        open={showAddItemModal}
        onOpenChange={(open) => { setShowAddItemModal(open); if (!open) { setInsertAfterId(null); setAddItemRoomContext(null); } }}
        activeCategory={activeCategory}
        activeSubcategories={activeSubcategories}
        selectedSubcategory={selectedSubcategory}
        setSelectedSubcategory={setSelectedSubcategory}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        newItem={newItem}
        setNewItem={setNewItem}
        filteredPriceList={filteredPriceList}
        isItemInEstimate={isItemInEstimate}
        onAddItemFromPriceList={handleAddItemFromPriceList}
        onAddManualItem={handleAddManualItem}
      />

      {/* Versions Modal */}
      <VersionsModal
        open={showVersionsModal}
        onOpenChange={setShowVersionsModal}
        versions={versions}
        currentId={id}
        onNavigateToVersion={(vId) => navigate(`/estimates/${vId}`)}
      />

      {/* Create Version Modal */}
      <CreateVersionModal
        open={showCreateVersionModal}
        onOpenChange={setShowCreateVersionModal}
        versionNote={versionNote}
        setVersionNote={setVersionNote}
        onCreateVersion={handleCreateVersion}
        creatingVersion={creatingVersion}
      />

      {/* Preview Modal */}
      <PreviewModal
        open={showPreviewModal}
        onOpenChange={setShowPreviewModal}
        previewMode={previewMode}
        previewHtml={previewHtml}
        loadingPreview={loadingPreview}
        onPreview={handlePreview}
        onDownloadPdf={handleDownloadPdf}
      />

      {/* FASE 4: Wizard impianto elettrico */}
      <ElectricalWizardModal
        open={showElectricalWizard}
        onClose={() => setShowElectricalWizard(false)}
        estimateId={id}
        onApplied={(updated) => {
          setEstimate(updated);
          setFormData(prev => {
            const nf = { ...prev, items: updated.items || [], category_notes: updated.category_notes || prev.category_notes };
            initialFormDataRef.current = JSON.stringify(nf);
            return nf;
          });
          setHasUnsavedChanges(false);
          setActiveCategory('D');
        }}
      />

      {/* FASE 5F: conferma reorder con modifiche non salvate */}
      <AlertDialog open={!!reorderConfirm} onOpenChange={(o) => { if (!o) setReorderConfirm(null); }}>
        <AlertDialogContent data-testid="reorder-unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>
              Sono presenti modifiche non salvate. Salva le modifiche prima di riordinare le voci.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              data-testid="reorder-cancel-btn"
              onClick={() => setReorderConfirm(null)}
            >
              Annulla
            </Button>
            <Button
              className="bg-[#FF7819] hover:bg-[#e66a12] text-white"
              data-testid="reorder-save-continue-btn"
              disabled={saving}
              onClick={handleReorderSaveAndContinue}
            >
              {saving ? 'Salvataggio...' : 'Salva e continua'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EstimateDetailPage;
